import { bopApi } from '@/lib/api/client';

// Shared service for the phase-11 brokers surface: list page, detail page (incl. the
// Transactions tab), and the transaction-batch detail page. Error envelope everywhere:
// `{ error: string }` on 400/403/404/500, surfaced via lib/api/error's getErrorMessage.

const BROKERS_PATH = 'v1/brokers';
const SETTINGS_PATH = 'v1/brokers/settings';

/** The legacy UI always sends limit=25 for every cursor-paginated broker list. */
export const BROKER_LIST_LIMIT = 25;

// ---------------------------------------------------------------------------
// Shared wire types
// ---------------------------------------------------------------------------

export type BrokerType = 'TIED_AGENT' | 'BROKER';

export type BrokerSetting =
  | 'NO_FUTURES_MANUAL_REVIEW'
  | 'NO_AML_REQUIRED' // upstream TODO: remove once migrated to aml risk level
  | 'AML_RISK_LEVEL_ONLY'
  | 'MINIMAL_PERSONAL_IDENTITY_DATA'
  | 'FUTURES_APPROPRIATENESS_RESULT_ONLY';

export interface PercentageFeeSplit {
  type: 'percentage';
  /** broker's % share of trade fees, 0–100, ≤2 dp */
  trades?: number;
  /** broker's % share of liquidation fees, 0–100, ≤2 dp */
  liquidations?: number;
}

export interface BasisPointsFeeSplit {
  type: 'basis_points';
  /** OT's maker fee in bps, -100–100, ≤2 dp (negative = rebate) */
  maker_fee_bps: number;
  /** OT's taker fee in bps, -100–100, ≤2 dp */
  taker_fee_bps: number;
  /** broker's % share of liquidation fees, 0–100, ≤2 dp */
  liquidations?: number;
}

export type FeeSplit = PercentageFeeSplit | BasisPointsFeeSplit;

/**
 * Dynamo key semantics: `p` is the constant partition key, `s` (sort key) is the
 * broker's account holder UUID — the identity used as React key and route param.
 * Never confuse `s` with `fast_id` (an encoded negative int64 string).
 */
export interface WireBroker {
  p: 'broker';
  s: string;
  fast_id: string;
  broker_type: BrokerType;
  /** lowercase-hyphen slug, e.g. "acme-broker" */
  name: string;
  /** ISO-8601 */
  created_at: string;
  /** ALWAYS present in responses (lambda does `?? []`) — keep defensive `?? []` at use sites anyway */
  settings: BrokerSetting[];
  /** omitted entirely when never configured (trading-stats sends `null` instead — handle both) */
  fee_split?: FeeSplit;
}

export type GetBrokersResponse = WireBroker[];

/**
 * Cursor pagination envelope for every broker list endpoint.
 * `cursor` is OMITTED when there are no more pages. Opaque — echo back via `?cursor=`.
 */
export interface PaginatedResponse<T> {
  items: T[];
  cursor?: string;
}

// ---------------------------------------------------------------------------
// Fee bands
// ---------------------------------------------------------------------------

export type FeeBandType = 'FIXED' | 'VOLUME';
export type FeeBandState = 'PENDING' | 'ACTIVE' | 'REJECTED';

export interface FixedFeeStructure {
  maker_fee_bps: number;
  taker_fee_bps: number;
}

export interface VolumeTier {
  /** volume threshold */
  volume: number;
  maker_fee_bps: number;
  taker_fee_bps: number;
}

export interface WireFeeBand {
  fee_band_id: string;
  name: string;
  type: FeeBandType;
  state: FeeBandState;
  /** object for FIXED, array for VOLUME */
  fee_structure: FixedFeeStructure | VolumeTier[];
  /** ISO-8601 */
  created_at: string;
  /** ISO-8601 */
  updated_at: string;
  /** present on REJECTED bands */
  rejection_reason?: string;
}

// ---------------------------------------------------------------------------
// Clients
// ---------------------------------------------------------------------------

export interface WireClientAccount {
  /** a UUID (lambda strips the `ca_` prefix) */
  client_account_id: string;
  fast_id: string;
  /** ISO-8601 */
  created_at: string;
  broker_reference?: string;
  product_eligibility?: string[];
}

// ---------------------------------------------------------------------------
// Trading stats & trades
// ---------------------------------------------------------------------------

export interface FeeByCurrency {
  currency: string;
  amount: number;
}

export interface TradingStatsPeriod {
  /** ISO-8601 (calendar month/week range start) */
  from: string;
  /** ISO-8601 */
  to: string;
  /** Σ instrument volumes converted to USD */
  volume_usd: number;
  trade_count: number;
  /** may be [] */
  fees: FeeByCurrency[];
}

export interface BrokerTradingStats {
  /** decoded from broker.fast_id (23-bit broker component) — will NOT visually match fast_id */
  broker_number: number;
  /** NOTE: null here (vs omitted on the broker object) */
  fee_split: FeeSplit | null;
  /** current calendar month */
  month: TradingStatsPeriod;
  /** current calendar week */
  week: TradingStatsPeriod;
}

export interface WireBrokerTrade {
  trade_id: string;
  order_id: string;
  /** e.g. "BTC_EUR" */
  instrument: string;
  side: 'BUY' | 'SELL';
  price: number;
  /** absolute order amount */
  amount: number;
  /** absolute matched amount — the UI's "Amount" column renders THIS */
  matched_amount: number;
  /** |matched| × price, computed server-side */
  notional: number;
  fee: number;
  fee_currency: string;
  taker: boolean;
  account_id: number;
  /** stringified FAST uid, or null */
  user_fast_id: string | null;
  /** ISO-8601 */
  timestamp: string;
}

// ---------------------------------------------------------------------------
// Transactions tab datasets
// ---------------------------------------------------------------------------

export interface WireBrokerTransaction {
  transaction_id: string;
  client_account_id: string;
  subaccount_id?: string;
  /** decimal STRING — render as-is, do not Number() it */
  amount: string;
  currency: string;
  transaction_reference: string;
  /** free-form; observed: PENDING, PROCESSING, COMPLETED, SUCCESS, CREDITED, FAILED, REJECTED */
  status: string;
  /** e.g. DEPOSIT / WITHDRAWAL variants */
  type: string;
  /** ISO-8601 */
  created_at: string;
  updated_at?: string;
}

export interface WireBrokerTransfer {
  transfer_id: string;
  source_account_id: string;
  destination_account_id: string;
  currency: string;
  /** decimal string — render verbatim */
  amount: string;
  transfer_type: string;
  /** ISO-8601 (NOTE: `timestamp`, not `created_at`) */
  timestamp: string;
}

export type TransactionBatchStatus = 'OPEN' | 'NOTIFIED' | 'SETTLED' | 'FAILED';

export interface WireTransactionBatch {
  /** may contain characters needing encodeURIComponent in URLs (Dynamo composite keys) */
  batch_id: string;
  currency: string;
  type: string;
  tx_count: number;
  /** NUMBERS here (unlike the string amounts on individual transactions) */
  total_amount: number;
  deposit_count: number;
  deposit_total: number;
  withdrawal_count: number;
  withdrawal_total: number;
  net_amount: number;
  status: TransactionBatchStatus;
  /** ISO-8601 */
  batch_window_start: string;
  /** ISO-8601 */
  batch_window_end: string;
  created_at: string;
  updated_at: string;
  /** present once settled */
  settled_at?: string;
}

/** The ONLY broker endpoint that wraps its payload. */
export interface GetTransactionBatchResponse {
  batch: WireTransactionBatch;
}

export interface WireBatchTransaction {
  transaction_id: string;
  client_account_id: string;
  subaccount_id?: string;
  /** decimal STRING — render verbatim */
  amount: string;
  currency: string;
  transaction_reference?: string;
  transaction_batch_id?: string;
  /** deposit/withdrawal variant */
  type: string;
  /** observed: PENDING, PROCESSING, COMPLETED, CREDITED, SUCCESS, FAILED, REJECTED */
  status: string;
  /** ISO-8601 */
  created_at: string;
  updated_at?: string;
}

// ---------------------------------------------------------------------------
// Request builders (the re-keying the Nuxt BFF used to do, now client-side)
// ---------------------------------------------------------------------------

/** Request body for POST /v1/brokers (Zod codes become the 400 error string). */
export interface CreateBrokerRequest {
  /** must be a UUID → 'INVALID_ACCOUNT_HOLDER_ID' */
  account_holder_id: string;
  /** min length 1 → 'INVALID_BROKER_NAME' */
  broker_name: string;
  broker_type: BrokerType;
  broker_settings?: BrokerSetting[];
  /** audit justification — optional at the API but mandatory client-side */
  comment?: string;
}

export interface CreateBrokerParams {
  accountHolderId: string;
  brokerName: string;
  brokerType: BrokerType;
  brokerSettings?: BrokerSetting[];
  comment: string;
}

/**
 * camelCase→snake_case field map from the legacy Nuxt BFF.
 * An empty settings selection must OMIT the `broker_settings` key entirely, never
 * send `broker_settings: []`.
 */
export const buildCreateBrokerRequest = ({ accountHolderId, brokerName, brokerType, brokerSettings, comment }: CreateBrokerParams): CreateBrokerRequest => ({
  account_holder_id: accountHolderId,
  broker_name: brokerName,
  broker_type: brokerType,
  comment,
  ...(brokerSettings && brokerSettings.length > 0 ? { broker_settings: brokerSettings } : {}),
});

/** Request body for PUT /v1/brokers/settings. */
export interface UpdateBrokerSettingsRequest {
  /** UUID → 'INVALID_BROKER_ID' */
  broker_id: string;
  /** REQUIRED, full desired state; [] clears all settings ('INVALID_BROKER_SETTING' per bad value) */
  settings: BrokerSetting[];
  /** optional discriminated union — validation codes FEE_SPLIT_* */
  fee_split?: FeeSplit;
  /** audit justification */
  comment?: string;
}

export interface UpdateBrokerSettingsParams {
  brokerId: string;
  settings: BrokerSetting[];
  feeSplit?: FeeSplit;
  comment?: string;
}

/**
 * camelCase→snake_case map from the legacy settings.put.ts:
 * `fee_split` only when defined, `comment` only when truthy.
 * SEMANTICS ARE FULL-STATE REPLACE — both `settings` and `fee_split` overwrite what is
 * stored; an unchecked box means "remove that setting".
 */
export const buildUpdateBrokerSettingsRequest = ({ brokerId, settings, feeSplit, comment }: UpdateBrokerSettingsParams): UpdateBrokerSettingsRequest => ({
  broker_id: brokerId,
  settings,
  ...(feeSplit !== undefined ? { fee_split: feeSplit } : {}),
  ...(comment ? { comment } : {}),
});

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

const listSearchParams = (cursor?: string): Record<string, string | number> => ({
  limit: BROKER_LIST_LIMIT,
  ...(cursor ? { cursor } : {}),
});

const getPaginated = <T>(path: string, cursor?: string): Promise<PaginatedResponse<T>> => bopApi.get(path, { searchParams: listSearchParams(cursor) }).json<PaginatedResponse<T>>();

/** GET /v1/brokers — raw JSON array, no pagination. */
export const getBrokers = (): Promise<GetBrokersResponse> => bopApi.get(BROKERS_PATH).json<GetBrokersResponse>();

/** POST /v1/brokers — 201 body is opaque; callers ignore it and refetch the list. */
export const createBroker = (request: CreateBrokerRequest): Promise<unknown> => bopApi.post(BROKERS_PATH, { json: request }).json<unknown>();

/** GET /v1/brokers/{brokerId} — 404 'BROKER_NOT_FOUND' must branch to the not-found panel. */
export const getBrokerDetail = (brokerId: string): Promise<WireBroker> => bopApi.get(`${BROKERS_PATH}/${brokerId}`).json<WireBroker>();

/** GET /v1/brokers/{brokerId}/fee-bands — read-only here; review happens via the broker-alert flow. */
export const getBrokerFeeBands = (brokerId: string, cursor?: string): Promise<PaginatedResponse<WireFeeBand>> =>
  getPaginated<WireFeeBand>(`${BROKERS_PATH}/${brokerId}/fee-bands`, cursor);

export const getBrokerClients = (brokerId: string, cursor?: string): Promise<PaginatedResponse<WireClientAccount>> =>
  getPaginated<WireClientAccount>(`${BROKERS_PATH}/${brokerId}/clients`, cursor);

/**
 * GET /v1/brokers/{brokerId}/trading-stats — SLOW endpoint: the lambda has a 29 s
 * timeout (two QuestDB range scans + a crypto-price fetch). The shared ky client's
 * 30 s timeout covers it — do not lower it for this call.
 */
export const getBrokerTradingStats = (brokerId: string): Promise<BrokerTradingStats> => bopApi.get(`${BROKERS_PATH}/${brokerId}/trading-stats`).json<BrokerTradingStats>();

/**
 * GET /v1/brokers/{brokerId}/trades — `from`/`to` are deliberately never sent
 * (legacy parity): the server defaults to a 90-day window anchored at "now". The
 * opaque cursor keeps position across pages.
 */
export const getBrokerTrades = (brokerId: string, cursor?: string): Promise<PaginatedResponse<WireBrokerTrade>> =>
  getPaginated<WireBrokerTrade>(`${BROKERS_PATH}/${brokerId}/trades`, cursor);

export const getBrokerDeposits = (brokerId: string, cursor?: string): Promise<PaginatedResponse<WireBrokerTransaction>> =>
  getPaginated<WireBrokerTransaction>(`${BROKERS_PATH}/${brokerId}/deposits`, cursor);

export const getBrokerWithdrawals = (brokerId: string, cursor?: string): Promise<PaginatedResponse<WireBrokerTransaction>> =>
  getPaginated<WireBrokerTransaction>(`${BROKERS_PATH}/${brokerId}/withdrawals`, cursor);

export const getBrokerTransfers = (brokerId: string, cursor?: string): Promise<PaginatedResponse<WireBrokerTransfer>> =>
  getPaginated<WireBrokerTransfer>(`${BROKERS_PATH}/${brokerId}/transfers`, cursor);

export const getBrokerTransactionBatches = (brokerId: string, cursor?: string): Promise<PaginatedResponse<WireTransactionBatch>> =>
  getPaginated<WireTransactionBatch>(`${BROKERS_PATH}/${brokerId}/transaction-batches`, cursor);

/**
 * GET /v1/brokers/{brokerId}/transaction-batches/{batchId} — unwraps the unique
 * `{ batch }` envelope so consumers see WireTransactionBatch directly.
 * `batchId` arrives DECODED from React Router; it is re-encoded exactly ONCE here —
 * ids can contain `#`/`:`-like Dynamo separators and ky does not encode interpolated
 * path segments.
 */
export const getBrokerTransactionBatch = async (brokerId: string, batchId: string): Promise<WireTransactionBatch> => {
  const response = await bopApi.get(`${BROKERS_PATH}/${brokerId}/transaction-batches/${encodeURIComponent(batchId)}`).json<GetTransactionBatchResponse>();
  return response.batch;
};

/** Same single-encode rule as getBrokerTransactionBatch. */
export const getBrokerBatchTransactions = (brokerId: string, batchId: string, cursor?: string): Promise<PaginatedResponse<WireBatchTransaction>> =>
  getPaginated<WireBatchTransaction>(`${BROKERS_PATH}/${brokerId}/transaction-batches/${encodeURIComponent(batchId)}/transactions`, cursor);

/**
 * PUT /v1/brokers/settings — full-state replace (see buildUpdateBrokerSettingsRequest).
 * 200 body is the opaque upstream result; callers ignore it and refetch the broker.
 * Real error codes (e.g. FEE_SPLIT_MAX_2_DECIMALS) surface via getErrorMessage —
 * the legacy BFF flattened them to a generic 500.
 */
export const updateBrokerSettings = (request: UpdateBrokerSettingsRequest): Promise<unknown> => bopApi.put(SETTINGS_PATH, { json: request }).json<unknown>();
